/**
 * Server state for the counter. The backend is the only source of money: every figure on screen comes from
 * the last `GET /counter/sessions/{id}/bill` (or the floor), and after every financial action the bill is
 * read again before the screen is trusted. Realtime events are refetch signals and never carry the bill.
 *
 * Recovery, in order: refetch on `stream.ready` (every connect and reconnect), on `stream.resync`, when the tab
 * becomes visible again, and after every action. While the stream is not live a slow poll keeps the screen
 * honest; while it is live there is no polling at all.
 *
 * Financial writes are never retried by the client. Each intent has one Idempotency-Key (`useIntentKey`):
 * after an uncertain failure, pressing the same action again with the same inputs reuses the key, so the
 * backend replays the original result instead of recording a second one; changing an input starts a new intent.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useStaffSession } from '@/auth/react';
import { useToast } from '@/components/Toast';
import { newIdempotencyKey } from '@/lib/ids';
import { useRealtimeEvent, useRealtimeState } from '@/realtime/react';
import { counterApi } from './api';
import type {
  AdjustmentInput,
  CloseInput,
  OpenSessionInput,
  PaymentInput,
  PrintResult,
} from './api';
import { acceptedTendersFrom, describeCounterError, describePrint } from './wording';
import { isApiError } from '@/api/errors';

/** Poll interval while the live stream is down. Not used while it is live. */
export const FALLBACK_POLL_MS = 30_000;
/** Events that arrive together become one refetch. */
export const COALESCE_MS = 100;

export const counterKeys = {
  floor: (branchId: string | null) => ['counter', branchId, 'floor'] as const,
  bills: (branchId: string | null) => ['counter', branchId, 'bill'] as const,
  bill: (branchId: string | null, sessionId: string | null) =>
    ['counter', branchId, 'bill', sessionId] as const,
  tenders: (branchId: string | null) => ['counter', branchId, 'tenders'] as const,
  assistance: (branchId: string | null) => ['counter', branchId, 'assistance'] as const,
};

function useBranchId(): string | null {
  return useStaffSession()?.user.branchId ?? null;
}

export function useFloor(enabled = true) {
  const branchId = useBranchId();
  const live = useRealtimeState() === 'live';
  return useQuery({
    queryKey: counterKeys.floor(branchId),
    queryFn: ({ signal }) => counterApi.getFloor(signal),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: live ? false : FALLBACK_POLL_MS,
  });
}

/**
 * Open help requests, read only while the floor says some session asked for help. The floor's own flag
 * decides whether a card shows a chip; this read only names what was asked for.
 */
export function useAssistance(enabled: boolean) {
  const branchId = useBranchId();
  const live = useRealtimeState() === 'live';
  return useQuery({
    queryKey: counterKeys.assistance(branchId),
    queryFn: ({ signal }) => counterApi.getAssistance(signal),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: live ? false : FALLBACK_POLL_MS,
  });
}

export function useBill(sessionId: string | null, enabled = true) {
  const branchId = useBranchId();
  const live = useRealtimeState() === 'live';
  return useQuery({
    queryKey: counterKeys.bill(branchId, sessionId),
    queryFn: ({ signal }) => counterApi.getBill(sessionId as string, signal),
    enabled: enabled && sessionId !== null,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: live ? false : FALLBACK_POLL_MS,
  });
}

/**
 * Maps staff stream events to refetches while the counter is open. Duplicate and bursty events are harmless:
 * they only mark a query for refetch, and marks made within `COALESCE_MS` run once. The cashier's audience
 * (see the backend's realtime audience map) has no `order.item_status_changed` or `cart.updated`; a manager
 * does, and the floor's "still in the kitchen" flag follows it.
 */
export function useCounterLive(enabled = true) {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const pending = useRef({ floor: false, bill: false, assistance: false });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = undefined;
    },
    [],
  );

  const mark = (what: 'floor' | 'bill' | 'assistance') => {
    if (!enabled) return;
    pending.current[what] = true;
    if (timer.current !== undefined) return;
    timer.current = setTimeout(() => {
      timer.current = undefined;
      const due = pending.current;
      pending.current = { floor: false, bill: false, assistance: false };
      if (due.floor) void queryClient.invalidateQueries({ queryKey: counterKeys.floor(branchId) });
      if (due.bill) void queryClient.invalidateQueries({ queryKey: counterKeys.bills(branchId) });
      if (due.assistance)
        void queryClient.invalidateQueries({ queryKey: counterKeys.assistance(branchId) });
    }, COALESCE_MS);
  };
  const both = () => {
    mark('floor');
    mark('bill');
  };
  const help = () => {
    mark('floor');
    mark('assistance');
  };

  useRealtimeEvent('bill.updated', both);
  useRealtimeEvent('payment.updated', both);
  useRealtimeEvent('order.created', both);
  useRealtimeEvent('order.cancelled', both);
  useRealtimeEvent('order.item_voided', both);
  useRealtimeEvent('order.item_status_changed', () => mark('floor'));
  useRealtimeEvent('session.opened', () => mark('floor'));
  useRealtimeEvent('session.transferred', both);
  useRealtimeEvent('session.closed', () => {
    both();
    mark('assistance');
  });
  useRealtimeEvent('assistance.requested', help);
  useRealtimeEvent('assistance.resolved', help);
  useRealtimeEvent('stream.resync', () => {
    both();
    mark('assistance');
  });
  useRealtimeEvent('stream.ready', () => {
    both();
    mark('assistance');
  });
}

/**
 * One Idempotency-Key per user intent. `keyFor(signature)` returns the same key while the signature (the
 * inputs of the action) is unchanged, and a new key as soon as it changes. `reset()` ends the intent after the
 * backend has answered it, so the next press is a new action and can never be mistaken for a replay.
 */
export function useIntentKey() {
  const current = useRef<{ signature: string; key: string } | null>(null);
  const keyFor = useCallback((signature: string) => {
    if (current.current?.signature !== signature) {
      current.current = { signature, key: newIdempotencyKey() };
    }
    return current.current.key;
  }, []);
  const reset = useCallback(() => {
    current.current = null;
  }, []);
  return { keyFor, reset };
}

/**
 * Refetches what a financial action may have changed. After a success the promise is returned, so the action
 * stays "pending" until the screen shows the backend's new state. After a failure the refetch runs in the
 * background: the cashier is told at once, and the bill shown is the backend's current one a moment later.
 */
function refreshCounter(queryClient: QueryClient, branchId: string | null, failed = false) {
  const bill = queryClient.invalidateQueries({ queryKey: counterKeys.bills(branchId) });
  const floor = queryClient.invalidateQueries({ queryKey: counterKeys.floor(branchId) });
  return failed ? undefined : Promise.all([bill, floor]);
}

/**
 * Which payment methods the branch accepts, from the backend's own refusal (see `TENDER_LIST_REQUEST`).
 * Nothing is recorded by this request. If it ever succeeded, a payment was recorded by mistake, so the bill is
 * reread and the screen fails closed with no methods to choose.
 */
export function useAcceptedTenders(billId: string | null, enabled: boolean) {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  return useQuery({
    queryKey: counterKeys.tenders(branchId),
    enabled: enabled && billId !== null,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      try {
        await counterApi.askAcceptedTenders(billId as string, newIdempotencyKey());
      } catch (error) {
        const list = acceptedTendersFrom(error);
        if (list) return list;
        throw error;
      }
      void queryClient.invalidateQueries({ queryKey: counterKeys.bills(branchId) });
      throw new Error('The payment methods could not be read.');
    },
  });
}

/** Errors stay with the sheet that shows them, so the cashier can correct the entry; success is announced here. */
export function useRecordPayment() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { billId: string; input: PaymentInput; idempotencyKey: string }) =>
      counterApi.recordPayment(vars.billId, vars.input, vars.idempotencyKey),
    onSuccess: () => toast.show('Payment recorded.', { tone: 'success' }),
    onError: (error) => {
      const accepted = acceptedTendersFrom(error);
      if (accepted) queryClient.setQueryData(counterKeys.tenders(branchId), accepted);
    },
    onSettled: (_data, error) => refreshCounter(queryClient, branchId, error !== null),
  });
}

export function useAddAdjustment() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { billId: string; input: AdjustmentInput; idempotencyKey: string }) =>
      counterApi.addAdjustment(vars.billId, vars.input, vars.idempotencyKey),
    onSuccess: () => toast.show('Adjustment applied.', { tone: 'success' }),
    onSettled: (_data, error) => refreshCounter(queryClient, branchId, error !== null),
  });
}

/** A finished print is reported from the print job's own status: a failed print is never shown as success. */
export function usePrintBill() {
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { billId: string; idempotencyKey: string }) =>
      counterApi.printBill(vars.billId, vars.idempotencyKey),
    onSuccess: (result: PrintResult) => {
      const outcome = describePrint(result.status, result.is_reprint);
      toast.show(outcome.message, {
        tone: outcome.ok ? 'success' : 'error',
        durationMs: outcome.ok ? 3000 : 8000,
      });
    },
    onError: (error) =>
      toast.show(describeCounterError(error, 'print'), { tone: 'error', durationMs: 8000 }),
  });
}

export function useCloseSession() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { sessionId: string; input: CloseInput }) =>
      counterApi.closeSession(vars.sessionId, vars.input),
    onSuccess: (result) =>
      toast.show(`${result.location_label} closed.`, { tone: 'success', durationMs: 4000 }),
    onSettled: (_data, error) => refreshCounter(queryClient, branchId, error !== null),
  });
}

export function useOpenSession() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { input: OpenSessionInput; idempotencyKey: string }) =>
      counterApi.openSession(vars.input, vars.idempotencyKey),
    onSuccess: (result) =>
      toast.show(`${result.location_label} opened.`, { tone: 'success', durationMs: 4000 }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: counterKeys.floor(branchId) }),
  });
}

/** True when the failure means the bill does not exist yet (nothing ordered), not that loading failed. */
export function isNoBillYet(error: unknown): boolean {
  return isApiError(error) && error.hasCode('BILL_NOT_FOUND');
}
